import { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import { fileBasenameFromUrl } from '../utils/fileUrl';

/**
 * Displays an image from an auth-protected URL (/api/files/view/:filename).
 * Fetches with Authorization header and shows via blob URL so <img> doesn't get 401.
 */
function AuthImage({ filename, alt = '', className = '', onError, ...props }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(!!filename);
  const [error, setError] = useState(false);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!filename) {
      setLoading(false);
      setSrc(null);
      setError(false);
      return undefined;
    }
    // Already a data/blob URL (local preview)
    if (/^(data:|blob:)/i.test(String(filename))) {
      setSrc(String(filename));
      setLoading(false);
      setError(false);
      return undefined;
    }
    const name = fileBasenameFromUrl(filename);
    if (!name) {
      setLoading(false);
      setError(true);
      onError?.();
      return undefined;
    }
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setLoading(true);
    setError(false);

    let cancelled = false;

    const load = async () => {
      const paths = [
        `/files/view/${encodeURIComponent(name)}`,
        `/files/${encodeURIComponent(name)}`,
      ];
      let lastErr = null;
      for (const path of paths) {
        try {
          const res = await api.get(path, {
            responseType: 'blob',
            skipErrorToast: true,
          });
          if (cancelled) return;
          const ct = String(res.headers?.['content-type'] || '');
          if (ct.includes('application/json')) {
            lastErr = new Error('File view returned JSON');
            continue;
          }
          // Some proxies return JSON error with image content-type missing
          if (res.data?.type === 'application/json') {
            lastErr = new Error('File view returned JSON blob');
            continue;
          }
          const objectUrl = URL.createObjectURL(res.data);
          blobUrlRef.current = objectUrl;
          setSrc(objectUrl);
          setError(false);
          return;
        } catch (e) {
          lastErr = e;
        }
      }
      if (cancelled) return;
      console.warn('[AuthImage] failed to load', name, lastErr);
      setError(true);
      setSrc(null);
      onError?.();
    };

    load().finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [filename]);

  if (!filename) return null;
  if (loading) return <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} {...props} />;
  if (error || !src) {
    return (
      <div
        className={`bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-[10px] ${className}`}
        title="Image failed to load"
        {...props}
      >
        No image
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} {...props} />;
}

export default AuthImage;
